<script setup lang="ts">
/**
 * App Component - Laumeris
 * Root component with toast notifications and cookie consent
 */

import { onMounted, computed } from 'vue'
import { useAuthStore, useUIStore, useThemeStore, useLocaleStore } from './stores'
import { useCookiesStore } from './stores/cookies'
import { useToast } from './composables'
import { Toast } from './components/ui'
import CookieBanner from './components/cookies/CookieBanner.vue'
import CookieSettings from './components/cookies/CookieSettings.vue'
import CookieButton from './components/cookies/CookieButton.vue'

const authStore = useAuthStore()
const uiStore = useUIStore()
const themeStore = useThemeStore()
const localeStore = useLocaleStore()
const cookiesStore = useCookiesStore()
const { toasts, removeToast } = useToast()

// Use themeStore for dark mode
const isDark = computed(() => themeStore.isDark)

onMounted(() => {
  // Initialize stores (theme and locale first for immediate UI)
  themeStore.initialize()
  localeStore.initialize()
  authStore.initialize()
  uiStore.initialize()
  cookiesStore.initialize()
})
</script>

<template>
  <div id="app" :class="{ 'dark': isDark }">
    <!-- Main Router View -->
    <router-view />

    <!-- Toast Notifications -->
    <Teleport to="body">
      <div class="fixed top-4 right-4 z-[100] space-y-3 pointer-events-none">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="pointer-events-auto"
        >
          <Toast
            :id="toast.id"
            :type="toast.type"
            :title="toast.title"
            :message="toast.message"
            :duration="toast.duration"
            @close="removeToast"
          />
        </div>
      </div>
    </Teleport>

    <!-- Cookie Consent (CNIL/RGPD) -->
    <CookieBanner />
    <CookieSettings />
    <CookieButton />
  </div>
</template>

<style>
/* Ensure dark mode class works on html element */
html.dark {
  color-scheme: dark;
}
</style>
